import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useInitiativeAutoSave } from '../../shared/useInitiativeAutoSave'
import Column from '@app/components/layout/Column'
import { useQuery } from '@based/sdk/react'
import { SelectableTagModel } from '@app/types/TagModel'
import { useTranslations } from '@i18n/useLanguage'
import { TagSelectionGroup } from '../blocks/TagSelectionGroup'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'

const CURRENT_STEP = initiativeCreationSteps.livingParameters

export const LivingParametersStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app
  const creationTranslations = translations.dashboard.initiative.creation

  const developmentFormsQuery = useQuery('list-development-forms')
  const targetGroupsQuery = useQuery('list-target-groups')
  const housingFormsQuery = useQuery('list-housing-forms')
  const interestsQuery = useQuery('list-user-interests')

  const allDevelopmentForms: SelectableTagModel<number>[] = mapNamed(
    developmentFormsQuery.data ?? [],
  )
  const [selectedDevelopmentForm, setSelectedDevelopmentForm] =
    useState<number>(initiative.developmentForm?.id)

  const allTargetGroups: SelectableTagModel<number>[] = mapNamed(
    targetGroupsQuery.data ?? [],
  )
  const [selectedTargetGroups, setSelectedTargetGroups] = useState<number[]>(
    initiative.targetAudiences.map((h) => h.id),
  )

  const allHousingForms: SelectableTagModel<number>[] = mapTitled(
    housingFormsQuery.data ?? [],
  )
  const [selectedHousingForms, setSelectedHousingForms] = useState<number[]>(
    initiative.housingForms.map((h) => h.id),
  )

  const allInterests: SelectableTagModel<number>[] = mapNamed(
    interestsQuery.data ?? [],
  )
  const [selectedInterests, setSelectedInterests] = useState<number[]>(
    initiative.interests.map((h) => h.id),
  )

  useInitiativeAutoSave(initiative.id, {
    developmentForm: selectedDevelopmentForm,
    targetAudiences: selectedTargetGroups,
    housingForms: selectedHousingForms,
    interests: selectedInterests,
    onboardingStep: CURRENT_STEP,
  })

  const selectTags = (
    id: number,
    setFunction: Dispatch<SetStateAction<number[]>>,
  ) => {
    setFunction((prev) => {
      const index = prev.indexOf(id)
      return index > -1 ? prev.toSpliced(index, 1) : [...prev, id]
    })
  }

  const selectTargetGroup = (id: number) =>
    selectTags(id, setSelectedTargetGroups)
  const selectHousingForm = (id: number) =>
    selectTags(id, setSelectedHousingForms)
  const selectInterest = (id: number) => selectTags(id, setSelectedInterests)

  useEffect(() => {
    if (
      selectedDevelopmentForm &&
      selectedTargetGroups.length > 0 &&
      selectedHousingForms.length > 0 &&
      selectedInterests.length > 0
    ) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [
    selectedDevelopmentForm,
    selectedTargetGroups,
    selectedHousingForms,
    selectedInterests,
    completeStep,
    uncompleteStep,
  ])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Column gap="2.5rem" alignItems="stetch">
        <TagSelectionGroup
          label={creationTranslations.developmentForm}
          subTitle={creationTranslations.selectOne}
          tags={allDevelopmentForms.map((f) => ({
            ...f,
            selected: selectedDevelopmentForm === f.key,
          }))}
          onSelect={setSelectedDevelopmentForm}
        />
        <TagSelectionGroup
          label={creationTranslations.targetGroups}
          subTitle={creationTranslations.selectOneOrMore}
          collapsedAmountVisible={Math.max(selectedTargetGroups.length, 5)}
          tags={allTargetGroups.map((f) => ({
            ...f,
            selected: selectedTargetGroups.includes(f.key),
          }))}
          onSelect={selectTargetGroup}
        />
        <TagSelectionGroup
          label={creationTranslations.housingForms}
          subTitle={creationTranslations.selectOneOrMore}
          collapsedAmountVisible={Math.max(selectedHousingForms.length, 5)}
          tags={allHousingForms.map((f) => ({
            ...f,
            selected: selectedHousingForms.includes(f.key),
          }))}
          onSelect={selectHousingForm}
        />
        <TagSelectionGroup
          label={creationTranslations.interests}
          subTitle={creationTranslations.selectOneOrMore}
          collapsedAmountVisible={Math.max(selectedInterests.length, 5)}
          tags={allInterests.map((f) => ({
            ...f,
            selected: selectedInterests.includes(f.key),
          }))}
          onSelect={selectInterest}
        />
      </Column>
    </CreationGuide>
  )
}

const mapTitled = (
  forms: {
    id: number
    title: string
  }[],
): SelectableTagModel<number>[] => {
  return forms.map((f) => ({
    key: f.id,
    value: f.title,
    selected: false,
  }))
}

const mapNamed = (
  forms: {
    id: number
    name: string
  }[],
): SelectableTagModel<number>[] => {
  return forms.map((f) => ({
    key: f.id,
    value: f.name,
    selected: false,
  }))
}
